import { GlobeAltIcon, MagnifyingGlassIcon, PlusIcon, QueueListIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { useEffect, useState } from 'react'

import {
  useGetCompanyConfigFileRawQuery,
  useGetCompanyConfigMetadataQuery,
  useGetDashboardAppDataQuery,
  useUploadCompanyConfigMutation,
} from '../../app/apiSlice'
import {
  useGetConfigTestReportQuery,
  useGetDataCollectionConfigsQuery,
  useGetTestCasesQuery,
} from '../../app/apiSliceConfigStudioApi'
import { AdminDataTableSection, AdminPage, AdminPageHeader, Loader } from '../../common/components'
import type { AppTestCaseGroup } from '../../common/components/ApplicationConfigPage/types'
import { Badge, Breadcrumbs, Button, Divider, Text } from '../../common/designs'
import { useEnhancedNavigate } from '../../common/hooks'
import type { RuleIn } from '../ConfigureDataCollectionAdvancedAdminPanelPage/types'
import ApplicationTable from './ApplicationTable'
import RulesWithoutTestCases from './RulesWithoutTestCases'
import SystemDiscoveryModal from './SystemDiscoveryModal'
import { getAppListFromTestCases } from './dataHelpers'
import { ConfigureDataCollectionSubPathEnum } from './types'
import useManageServerTestCasesAndConfigs from './useManageServerTestCasesAndConfigs'

const ConfigureDataCollection = () => {
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const [applicationList, setApplicationList] = useState<AppTestCaseGroup[]>([])
  const [rulesWithoutAppInfo, setRulesWithoutAppInfo] = useState<RuleIn[]>([])
  const [isSystemDiscoveryModalOpen, setIsSystemDiscoveryModalOpen] = useState<boolean>(false)

  const { data: requirementsData, isLoading: isLoadingRequirementsData } = useGetTestCasesQuery()
  const { data: applicationMetaData, isLoading: isLoadingApplicationMetaData } =
    useGetDashboardAppDataQuery()
  const { data: ruleData, isLoading: isLoadingRuleData } = useGetDataCollectionConfigsQuery()
  const { data: configFileReport, isLoading: isLoadingConfigFileReport } =
    useGetConfigTestReportQuery()
  const { data: configFileRaw, isLoading: isLoadingConfigFileRaw } =
    useGetCompanyConfigFileRawQuery()
  const { data: companyConfigMetadata, isLoading: isLoadingCompanyConfigMetadata } =
    useGetCompanyConfigMetadataQuery()

  const [uploadCompanyConfig, uploadCompanyConfigRes] = useUploadCompanyConfigMutation()
  const { runConfigTests, isRunningConfigTests } = useManageServerTestCasesAndConfigs()

  // Update selectedAppTestCases if user modifies them.
  useEffect(() => {
    const { appData, rulesWithoutAppInfo } = getAppListFromTestCases(
      requirementsData?.test_cases ?? [],
      applicationMetaData ?? [],
      ruleData?.rules ?? [],
      configFileReport,
    )
    setApplicationList(appData.sort((a, b) => a.appName.localeCompare(b.appName)))
    setRulesWithoutAppInfo(rulesWithoutAppInfo)
  }, [requirementsData, applicationMetaData, ruleData, configFileReport])

  const updateText = companyConfigMetadata?.lastUpdateDate
    ? `${formatDistanceToNow(new Date(companyConfigMetadata?.lastUpdateDate))} ago`
    : 'never'

  const lastTestRunText = configFileReport?.saved_datetime_utc
    ? `${formatDistanceToNow(new Date(configFileReport.saved_datetime_utc + 'Z'))} ago`
    : 'never'

  const configFileObject = { ...configFileRaw }
  const deployedConfigVersion: string | undefined = configFileObject?.settings_properties?.version
  const configFileUpToDate = parseInt(deployedConfigVersion ?? '0') === ruleData?.version
  const testsUpToDate =
    parseInt(configFileReport?.test_case_version ?? '0') === requirementsData?.version &&
    parseInt(configFileReport?.settings_version ?? '0') === ruleData?.version
  const testsPass =
    (configFileReport?.is_passed ?? true) || (configFileReport?.failing_tests ?? []).length === 0
  const noTestsDefined =
    !(configFileReport?.is_passed ?? true) && (configFileReport?.failing_tests ?? [1]).length === 0

  return (
    <AdminPage width='full'>
      <Breadcrumbs
        variant='secondary'
        items={[
          {
            label: 'Configure Data Collection',
          },
        ]}
      />

      <Divider className='my-4' color='lightGray' />

      {isLoadingRequirementsData ||
      isLoadingApplicationMetaData ||
      isLoadingRuleData ||
      isLoadingConfigFileReport ||
      isLoadingCompanyConfigMetadata ||
      isLoadingConfigFileRaw ||
      isRunningConfigTests ||
      uploadCompanyConfigRes.isLoading ? (
        <Loader />
      ) : (
        <>
          <AdminPageHeader
            title='Configure Data Collection'
            description='Publish saved rules to agents, run automated config tests, and maintain rules per tracked application.'
          />
          <div className='mt-4 flex flex-wrap gap-2'>
            <Button
              size='s'
              iconStart={<PlusIcon />}
              onClick={() => navigate(ConfigureDataCollectionSubPathEnum.ADD_CUSTOM_APP)}
            >
              Track New Application
            </Button>
            <Button
              variant='secondary'
              size='s'
              iconStart={<QueueListIcon />}
              onClick={() => navigate(ConfigureDataCollectionSubPathEnum.APP_CATALOG)}
            >
              View Application Catalog
            </Button>
            <Button
              variant='secondary'
              size='s'
              onClick={() => setIsSystemDiscoveryModalOpen(true)}
              iconStart={<MagnifyingGlassIcon />}
            >
              Start System Discovery
            </Button>

            <Button
              variant='secondary'
              size='s'
              iconStart={<GlobeAltIcon />}
              onClick={() => navigate(ConfigureDataCollectionSubPathEnum.GLOBAL_DISCOVERY_RULES)}
            >
              Global Discovery Rules
            </Button>

            <Button
              variant='white'
              size='s'
              onClick={() => navigate(ConfigureDataCollectionSubPathEnum.CONFIG_PORTAL)}
            >
              Advanced Setup
            </Button>
          </div>

          <div
            className={clsx(
              'mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
              !configFileUpToDate && 'ring-2 ring-amber-300/50',
            )}
          >
            {!configFileUpToDate ? (
              <div className='border-b border-amber-200/80 bg-amber-50 px-3 py-1.5'>
                <Text size='xs' color='gray' className='leading-tight'>
                  Publish to push your latest rule changes to agents.
                </Text>
              </div>
            ) : null}

            <div className='grid grid-cols-1 bg-slate-50/50 md:grid-cols-2'>
              <section
                className='min-w-0 border-gray-200 px-4 py-2.5 md:border-r'
                aria-labelledby='configure-dc-deployment-heading'
              >
                <div className='flex min-w-0 items-start justify-between gap-3'>
                  <div className='min-w-0 flex-1 pr-1'>
                    <h2
                      id='configure-dc-deployment-heading'
                      className='font-inter text-base font-bold leading-tight text-gray-900'
                    >
                      Data collection configs
                    </h2>
                    <Text
                      variant='span'
                      size='xs'
                      weight={500}
                      color='lightGray'
                      className='mt-0.5 block max-w-full whitespace-normal break-words leading-snug'
                    >
                      {configFileUpToDate
                        ? 'Rules up-to-date — agents are running your latest saved rules.'
                        : 'Latest changes to rules are unpublished — publish to push them to agents'}
                    </Text>
                  </div>
                  <div className='flex shrink-0 flex-nowrap items-start justify-end gap-2 pt-0.5'>
                    {configFileUpToDate ? (
                      <Badge variant='green' size='s' className='shrink-0'>
                        Published
                      </Badge>
                    ) : (
                      <Button size='xs' className='shrink-0' onClick={() => uploadCompanyConfig()}>
                        Publish
                      </Button>
                    )}
                  </div>
                </div>
              </section>

              <section
                className='min-w-0 border-t border-gray-200 px-4 py-2.5 md:border-t-0'
                aria-labelledby='configure-dc-testing-heading'
              >
                <div className='flex min-w-0 items-start justify-between gap-3'>
                  <div className='min-w-0 flex-1 pr-1'>
                    <h2
                      id='configure-dc-testing-heading'
                      className='font-inter text-base font-bold leading-tight text-gray-900'
                    >
                      Config tests
                    </h2>
                    <Text
                      variant='span'
                      size='xs'
                      weight={500}
                      color='lightGray'
                      className='mt-0.5 block max-w-full whitespace-normal break-words leading-snug'
                    >
                      Run tests to verify your saved rules against the test cases
                    </Text>
                  </div>
                  <div className='shrink-0 pt-0.5'>
                    <Button
                      variant='secondary'
                      size='xs'
                      className='shrink-0'
                      onClick={() => runConfigTests()}
                    >
                      Run tests
                    </Button>
                  </div>
                </div>
              </section>
            </div>

            <div className='border-t border-gray-200' />

            <div className='grid grid-cols-1 bg-slate-50/80 md:grid-cols-2'>
              <div className='flex min-w-0 items-center border-gray-200 px-4 py-2.5 md:border-r'>
                <div className='flex min-w-0 flex-1 items-center justify-between gap-3'>
                  <div className='min-w-0 flex-1 text-xs leading-relaxed text-gray-600'>
                    <Text variant='span' size='xs' weight={500} color='lightGray'>
                      Last deployed
                    </Text>{' '}
                    <strong className='text-gray-800'>{updateText}</strong>
                  </div>
                  {!configFileUpToDate ? (
                    <div className='shrink-0'>
                      <Badge variant='orange' size='s'>
                        Unpublished
                      </Badge>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className='flex min-w-0 items-center border-t border-gray-200 px-4 py-2.5 md:border-t-0'>
                <div className='flex w-full min-w-0 items-center justify-start'>
                  <div className='flex min-w-0 max-w-xl flex-nowrap items-center gap-x-8 text-xs'>
                    <div className='min-w-0 max-w-44 shrink truncate whitespace-nowrap'>
                      <Text variant='span' size='xs' weight={500} color='lightGray'>
                        Last run
                      </Text>{' '}
                      <span className='font-medium text-gray-900'>{lastTestRunText}</span>
                    </div>
                    <div className='min-w-0 max-w-[7.5rem] shrink truncate whitespace-nowrap'>
                      <Text variant='span' size='xs' weight={500} color='lightGray'>
                        Status
                      </Text>{' '}
                      <Text
                        size='xs'
                        variant='span'
                        className={
                          testsUpToDate
                            ? 'font-medium text-green-600'
                            : 'font-medium text-amber-600'
                        }
                      >
                        {testsUpToDate ? 'Up to date' : 'Stale'}
                      </Text>
                    </div>
                    <div className='min-w-0 max-w-24 shrink truncate whitespace-nowrap'>
                      <Text variant='span' size='xs' weight={500} color='lightGray'>
                        Result
                      </Text>{' '}
                      <Text
                        size='xs'
                        variant='span'
                        className={
                          noTestsDefined
                            ? 'font-medium text-amber-600'
                            : testsPass
                              ? 'font-medium text-green-600'
                              : 'font-medium text-red-600'
                        }
                      >
                        {noTestsDefined ? 'None' : testsPass ? 'Pass' : 'Fail'}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <AdminDataTableSection className='mt-6'>
            <ApplicationTable applicationList={applicationList} />
            <RulesWithoutTestCases rulesWithoutAppInfo={rulesWithoutAppInfo} />
          </AdminDataTableSection>
        </>
      )}
      <SystemDiscoveryModal
        open={isSystemDiscoveryModalOpen}
        onClose={() => setIsSystemDiscoveryModalOpen(false)}
      />
    </AdminPage>
  )
}

export default ConfigureDataCollection

